import { createBrowserRouter, redirect } from "react-router";
import { useAuthStore } from "../stores/authStore";
import { AuthGuard } from "./components/AuthGuard";
import { MainLayout } from "./components/layout/MainLayout";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { Catalogs } from "./pages/Catalogs";
import { Profiles } from "./pages/Profiles";
import { Mappings } from "./pages/Mappings";
import { ComponentLibrary } from "./pages/ComponentLibrary";
import { SSPEditor } from "./pages/SSPEditor";
import { AssessmentPlans } from "./pages/AssessmentPlans";
import { AssessmentPlanDetail } from "./pages/AssessmentPlanDetail";
import { AssessmentPlanWizard } from "./pages/AssessmentPlanWizard";
import { AssessmentResults } from "./pages/AssessmentResults";
import { AssessmentResultDetail } from "./pages/AssessmentResultDetail";
import { AssessmentExecutionList } from "./pages/AssessmentExecutionList";
import { AssessmentExecution } from "./pages/AssessmentExecution";
import { AssessmentReviewList } from "./pages/AssessmentReviewList";
import { AssessmentReview } from "./pages/AssessmentReview";
import { POAM } from "./pages/POAM/POAM";
import { POAMDetail } from "./pages/POAMDetail";
import { POAMItemDetail } from "./pages/POAMItemDetail";
import { Telemetry } from "./pages/Telemetry";
import { Governance } from "./pages/Governance";
import { AuditLogs } from "./pages/AuditLogs";
import { Users } from "./pages/Users"; 
import { Settings } from "./pages/Settings";
import { LicenseManagement } from "./pages/LicenseManagement";
import {
  ContentRegistry,
  ProcessingConsolePage,
  ProcessingConsole3DPage,
} from "./pages/ContentRegistry";
import { ComponentDefinitionVisualizer } from "./pages/visualizer/ComponentDefinitionVisualizer";
import { DirectoryConfiguration } from "./pages/DirectoryConfiguration";
import { OrganizationManagement } from "./pages/OrganizationManagement";
import { RoleManagement } from "./pages/RoleManagement";
import { UserManagement } from "./pages/UserManagement";
import { CISODashboard } from "./pages/CISODashboard";
// import { GRCDashboard } from "./pages/GRCDashboard"; // replaced by GRCDashboardNew
// import { GRCDashboardNew } from "./pages/GRCDashboardNew"; // now the per-org drill-down inside GRCManagerDashboard
import { GRCManagerDashboard } from "./pages/GRCManagerDashboard/GRCManagerDashboard";
import { GroupAuditorDashboard } from "./pages/GroupAuditorDashboard/GroupAuditorDashboard";
import { SystemOwnerDashboard } from "./pages/SystemOwnerDashboard/SystemOwnerDashboard";
import { DashboardRouter } from "./pages/DashboardRouter";
import { QuadrantDashboard } from "./pages/QuadrantDashboard";
import { ExecutiveInsights } from "./pages/ExecutiveInsights";
import { ReportsList } from "./pages/ReportsList";
import { ReportDetails } from "./pages/ReportDetails";
import { AuditReport } from "./pages/AuditReport/AuditReport";
import { AuditReportDetail } from "./pages/AuditReport/AuditReportDetail";
import { SystemOwnerReport } from "./pages/SystemOwnerReport/SystemOwnerReport";
import { SystemOwnerReportDetail } from "./pages/SystemOwnerReport/SystemOwnerReportDetail";
import { CMDBApplications } from "./pages/CMDBApplications";
import { CMDBApplicationDetail } from "./pages/CMDBApplicationDetail";
import { CMDBApplicationAssetDetail } from "./pages/CMDBApplicationAssetDetail";
import { CMDBAssets } from "./pages/CMDBAssets";
import { CMDBConfigItems } from "./pages/CMDBConfigItems";
import { CMDBServices } from "./pages/CMDBServices";
import { CMDBServiceDetail } from "./pages/CMDBServiceDetail";
import { CMDBCloudAccounts } from "./pages/CMDBCloudAccounts";
import { CMDBCloudAccountDetail } from "./pages/CMDBCloudAccountDetail";
import { CMDBDeviceDetail } from "./pages/CMDBDeviceDetail";
import { LlmConfigurations } from "./pages/LlmConfigurations";
import { AiInteractionController } from "./pages/AiInteractionController";
import { ValidationRules } from "./pages/ValidationRules";
import { DashboardConfig } from "./pages/DashboardConfig/DashboardConfig";
import { DriftAnalysisWorkspace } from "./pages/grc/DriftAnalysisWorkspace";
import { ArchitectureCompliance } from "./pages/ArchitectureCompliance";
import { CisoInsights } from "./pages/CisoInsights";
import { Schedulers as AdminSchedulers } from "./pages/schedulers/Schedulers";
import { Tools } from "./pages/tools/Tools";
import { SeverityColors } from "./pages/severityColors/SeverityColors";
import { ToolConfig } from "./pages/tools/toolConfig/ToolConfig";
import { AlertCalendar } from "./pages/AlertCalendar/AlertCalendar";
import { SchedulerConfiguration } from "./pages/SchedulerConfiguration";
import { ChangeAssuranceGovernancePage } from "./pages/ChangeAssuranceGovernance/ChangeAssuranceGovernancePage";
import { LogReviewPage } from "./pages/LogReview/LogReviewPage";
import { LogReviewerInsightsPage } from "./pages/LogReviewerInsights/LogReviewerInsightsPage";
import { CisoLogReviewPage } from "./pages/CisoLogReview/CisoLogReviewPage";
import { IamPolicyCategorization } from "./pages/PrivilegeAccessGovernance/IamPolicyCategorization";
import { AgentHealth } from "./pages/AgentHealth";
import { AssetCoverageOnboarding } from "./pages/AssetCoverageOnboarding";
import { SkeletonPage } from "./components/Skeleton";
import { RouteErrorBoundary } from "./components/ErrorBoundary";
// import { GRCDashboard } from "./pages/GRCDashboard"; // replaced by GRCDashboardNew
// import { GRCDashboardNew } from "./pages/GRCDashboardNew"; // now the per-org drill-down inside GRCManagerDashboard
import { PrivilegeAccessGovernancePage } from "./pages/PrivilegeAccessGovernance/PrivilegeAccessGovernancePage";
import { UserAccessGovernance } from "./pages/PrivilegeAccessGovernance/UserAccessGovernance";
import { PrivilegeAccessInsights } from "./pages/PrivilegeAccessGovernance/PrivilegeAccessInsights";
import { AccessGovernance } from "./pages/PrivilegeAccessGovernance/AccessGovernance";
import { AccessReview } from "./pages/PrivilegeAccessGovernance/AccessReview";
import { PgaScheduler } from "./pages/PrivilegeAccessGovernance/PgaScheduler";
import { PrivilegeAccountConfiguration } from "./pages/PrivilegeAccessGovernance/PrivilegeAccountConfiguration";

export const router = createBrowserRouter([
  {
    path: "/login",
    HydrateFallback: SkeletonPage,
    ErrorBoundary: RouteErrorBoundary,
    lazy: () => import("./pages/Login").then((m) => ({ Component: m.Login })),
  },
  {
    path: "/",
    Component: AuthGuard,
    ErrorBoundary: RouteErrorBoundary,
    children: [
      {
        Component: MainLayout,
        children: [
          { index: true, loader: () => redirect("/dashboard/executive-insights") },
          {
            path: "dashboard",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/DashboardRouter").then((m) => ({ Component: m.DashboardRouter })),
          },
          {
            path: "dashboard/platform-owner",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/Dashboard").then((m) => ({ Component: m.Dashboard })),
          },
          {
            path: "dashboard/security-posture-dashboard",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/CISODashboard").then((m) => ({ Component: m.CISODashboard })),
          },
          {
            path: "dashboard/grc-manager",
            HydrateFallback: SkeletonPage,
            lazy: () =>
              import("./pages/GRCManagerDashboard/GRCManagerDashboard").then((m) => ({
                Component: m.GRCManagerDashboard,
              })),
          },
          {
            path: "dashboard/group-auditor",
            HydrateFallback: SkeletonPage,
            lazy: () =>
              import("./pages/GroupAuditorDashboard/GroupAuditorDashboard").then((m) => ({
                Component: m.GroupAuditorDashboard,
              })),
          },
          {
            path: "dashboard/system-owner",
            HydrateFallback: SkeletonPage,
            lazy: () =>
              import("./pages/SystemOwnerDashboard/SystemOwnerDashboard").then((m) => ({
                Component: m.SystemOwnerDashboard,
              })),
          },
          {
            path: "dashboard/quadrant",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/QuadrantDashboard").then((m) => ({ Component: m.QuadrantDashboard })),
          },
          {
            path: "dashboard/executive-insights",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/ExecutiveInsights").then((m) => ({ Component: m.ExecutiveInsights })),
          },
          {
            path: "continuous-compliance/governance/catalog",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/Catalogs").then((m) => ({ Component: m.Catalogs })),
          },
          {
            path: "continuous-compliance/governance/catalog/:id",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/Catalogs").then((m) => ({ Component: m.Catalogs })),
          },
          {
            path: "continuous-compliance/governance/profile",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/Profiles").then((m) => ({ Component: m.Profiles })),
          },
          {
            path: "continuous-compliance/governance/profile/:id",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/Profiles").then((m) => ({ Component: m.Profiles })),
          },
          {
            path: "continuous-compliance/governance/control-mapping",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/Mappings").then((m) => ({ Component: m.Mappings })),
          },
          {
            path: "continuous-compliance/governance/control-mapping/:id",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/Mappings").then((m) => ({ Component: m.Mappings })),
          },
          {
            path: "continuous-compliance/system/component-definition",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/ComponentLibrary").then((m) => ({ Component: m.ComponentLibrary })),
          },
          {
            path: "continuous-compliance/system/component-definition/:id",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/ComponentLibrary").then((m) => ({ Component: m.ComponentLibrary })),
          },
          {
            path: "continuous-compliance/system/system-security-plan",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/SSPEditor").then((m) => ({ Component: m.SSPEditor })),
          },
          {
            path: "continuous-compliance/system/system-security-plan/:id",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/SSPEditor").then((m) => ({ Component: m.SSPEditor })),
          },
          {
            path: "continuous-compliance/assessment/assessment-plan",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/AssessmentPlans").then((m) => ({ Component: m.AssessmentPlans })),
          },
          {
            path: "continuous-compliance/assessment/assessment-plan/wizard",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/AssessmentPlanWizard").then((m) => ({ Component: m.AssessmentPlanWizard })),
          },
          {
            path: "continuous-compliance/assessment/assessment-plan/:id",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/AssessmentPlanDetail").then((m) => ({ Component: m.AssessmentPlanDetail })),
          },
          {
            path: "continuous-compliance/assessment/assessment-execution",
            HydrateFallback: SkeletonPage,
            lazy: () =>
              import("./pages/AssessmentExecutionList").then((m) => ({ Component: m.AssessmentExecutionList })),
          },
          {
            path: "continuous-compliance/assessment/assessment-execution/:id",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/AssessmentExecution").then((m) => ({ Component: m.AssessmentExecution })),
          },
          {
            path: "continuous-compliance/assessment/assessment-review",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/AssessmentReviewList").then((m) => ({ Component: m.AssessmentReviewList })),
          },
          {
            path: "continuous-compliance/assessment/assessment-review/:id",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/AssessmentReview").then((m) => ({ Component: m.AssessmentReview })),
          },
          {
            path: "continuous-compliance/assessment/assessment-result",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/AssessmentResults").then((m) => ({ Component: m.AssessmentResults })),
          },
          {
            path: "continuous-compliance/assessment/assessment-result/:id",
            HydrateFallback: SkeletonPage,
            lazy: () =>
              import("./pages/AssessmentResultDetail").then((m) => ({ Component: m.AssessmentResultDetail })),
          },
          {
            path: "continuous-compliance/assessment/drift-management",
            HydrateFallback: SkeletonPage,
            lazy: () =>
              import("./pages/grc/DriftAnalysisWorkspace").then((m) => ({ Component: m.DriftAnalysisWorkspace })),
          },
          {
            path: "continuous-compliance/risk/poam",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/POAM/POAM").then((m) => ({ Component: m.POAM })),
          },
          {
            path: "continuous-compliance/risk/poam/:id",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/POAMDetail").then((m) => ({ Component: m.POAMDetail })),
          },
          {
            path: "continuous-compliance/risk/poam/:id/item/:itemId",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/POAMItemDetail").then((m) => ({ Component: m.POAMItemDetail })),
          },
          {
            path: "continuous-compliance/scheduler",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/common-scheduler/SchedulerPage").then((m) => ({ Component: m.SchedulerPage })),
          },
          {
            path: "privilege-governance-assurance",
            Component: PrivilegeAccessGovernancePage,
            children: [
              {
                index: true,
                loader: () => redirect("/privilege-governance-assurance/privilege-access-insights"),
              },
              { path: "privilege-access-insights", Component: UserAccessGovernance },
              { path: "privilege-governance-assurance", Component: UserAccessGovernance },
              { path: "user-access-review", Component: UserAccessGovernance },
              { path: "privilege-insights", Component: PrivilegeAccessInsights },
              { path: "access-review", Component: AccessReview },
              { path: "access-governance", Component: AccessGovernance },
              {
                path: "risk-configuration",
                loader: () => redirect("/privilege-governance-assurance/account-configuration"),
              },
              { path: "account-configuration", Component: PrivilegeAccountConfiguration },
              { path: "pga-configuration", Component: PrivilegeAccountConfiguration },
              { path: "schedulers", Component: PgaScheduler },
              {
                path: "iam-policy-categorization",
                loader: () =>
                  redirect("/privilege-governance-assurance/account-configuration?tab=iam-policy-categorization"),
              },
            ],
          },
          {
            path: "privilege-access-governance/*",
            loader: ({ request }) => {
              const url = new URL(request.url);
              const rest = url.pathname.replace(/^\/privilege-access-governance/, "");
              return redirect(`/privilege-governance-assurance${rest || "/privilege-access-insights"}`);
            },
          },
          {
            path: "report",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/ReportsList").then((m) => ({ Component: m.ReportsList })),
          },
          {
            path: "report/:sspId",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/ReportDetails").then((m) => ({ Component: m.ReportDetails })),
          },
          { path: "audit-report", Component: AuditReport },
          { path: "audit-report/:sspId", Component: AuditReportDetail },
          // One assessment plan's report — same page, assessment-scoped stream.
          {
            path: "audit-report/:sspId/assessment/:assessmentId",
            Component: AuditReportDetail,
          },
          { path: "system-owner-report", Component: SystemOwnerReport },
          { path: "system-owner-report/:sspId", Component: SystemOwnerReportDetail },
          {
            path: "telemetry",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/Telemetry").then((m) => ({ Component: m.Telemetry })),
          },
          {
            path: "continuous-compliance/governance/content-registry",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/ContentRegistry").then((m) => ({ Component: m.ContentRegistry })),
          },
          {
            path: "continuous-compliance/governance/content-registry/console",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/ContentRegistry").then((m) => ({ Component: m.ProcessingConsolePage })),
          },
          {
            path: "continuous-compliance/governance/content-registry/console-3d",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/ContentRegistry").then((m) => ({ Component: m.ProcessingConsole3DPage })),
          },
          {
            path: "continuous-compliance/governance/content-registry/:id",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/ContentRegistry").then((m) => ({ Component: m.ContentRegistry })),
          },
          {
            path: "governance",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/Governance").then((m) => ({ Component: m.Governance })),
          },
          {
            path: "configuration/audit-log",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/AuditLogs").then((m) => ({ Component: m.AuditLogs })),
          },
          {
            path: "configuration/administration/schedulers/:id/configuration",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/SchedulerConfiguration").then((m) => ({ Component: m.SchedulerConfiguration })),
          },
          {
            path: "user-management",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/Users").then((m) => ({ Component: m.Users })),
          },
          {
            path: "setting",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/Settings").then((m) => ({ Component: m.Settings })),
          },
          {
            path: "configuration/administration/license-management",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/LicenseManagement").then((m) => ({ Component: m.LicenseManagement })),
          },
          {
            path: "component-definition-visualizer",
            HydrateFallback: SkeletonPage,
            lazy: () =>
              import("./pages/visualizer/ComponentDefinitionVisualizer").then((m) => ({
                Component: m.ComponentDefinitionVisualizer,
              })),
          },
          {
            path: "configuration/cmdb/applications",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/CMDBApplications").then((m) => ({ Component: m.CMDBApplications })),
          },
          {
            path: "configuration/cmdb/applications/:id",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/CMDBApplicationDetail").then((m) => ({ Component: m.CMDBApplicationDetail })),
          },
          {
            path: "configuration/cmdb/applications/:applicationId/assets/:assetId",
            HydrateFallback: SkeletonPage,
            lazy: () =>
              import("./pages/CMDBApplicationAssetDetail").then((m) => ({
                Component: m.CMDBApplicationAssetDetail,
              })),
          },
          {
            path: "configuration/cmdb/assets",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/CMDBAssets").then((m) => ({ Component: m.CMDBAssets })),
          },
          {
            path: "configuration/cmdb/devices",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/CMDBConfigItems").then((m) => ({ Component: m.CMDBConfigItems })),
          },
          {
            path: "configuration/cmdb/devices/:id",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/CMDBDeviceDetail").then((m) => ({ Component: m.CMDBDeviceDetail })),
          },
          {
            path: "configuration/cmdb/services",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/CMDBServices").then((m) => ({ Component: m.CMDBServices })),
          },
          {
            path: "configuration/cmdb/services/:id",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/CMDBServiceDetail").then((m) => ({ Component: m.CMDBServiceDetail })),
          },
          {
            path: "configuration/cmdb/cloud-accounts",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/CMDBCloudAccounts").then((m) => ({ Component: m.CMDBCloudAccounts })),
          },
          {
            path: "configuration/cmdb/cloud-accounts/:id",
            HydrateFallback: SkeletonPage,
            lazy: () =>
              import("./pages/CMDBCloudAccountDetail").then((m) => ({ Component: m.CMDBCloudAccountDetail })),
          },
          {
            path: "configuration/settings/directory-configuration",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/DirectoryConfiguration").then((m) => ({ Component: m.DirectoryConfiguration })),
          },
          {
            path: "configuration/administration/organization",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/OrganizationManagement").then((m) => ({ Component: m.OrganizationManagement })),
          },
          {
            path: "configuration/administration/roles",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/RoleManagement").then((m) => ({ Component: m.RoleManagement })),
          },
          {
            path: "configuration/administration/users",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/UserManagement").then((m) => ({ Component: m.UserManagement })),
          },
          {
            path: "configuration/administration/llm-configuration",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/LlmConfigurations").then((m) => ({ Component: m.LlmConfigurations })),
          },
          {
            path: "configuration/administration/ai-module-configuration",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/AiInteractionController").then((m) => ({ Component: m.AiInteractionController })),
          },
          {
            path: "configuration/administration/schedulers",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/schedulers/Schedulers").then((m) => ({ Component: m.Schedulers })),
          },
          {
            path: "configuration/administration/tools",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/tools/Tools").then((m) => ({ Component: m.Tools })),
          },
          {
            path: "configuration/administration/tools/instance/:toolName?",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/tools/ToolsVendorTable").then((m) => ({ Component: m.ToolsVendorTable })),
          },
          {
            path: "configuration/administration/tools/config/:toolConfigId",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/tools/toolConfig/ToolConfig").then((m) => ({ Component: m.ToolConfig })),
          },
          {
            path: "configuration/administration/severity-colors",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/severityColors/SeverityColors").then((m) => ({ Component: m.SeverityColors })),
          },
          {
            path: "configuration/administration/validation-rules",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/ValidationRules").then((m) => ({ Component: m.ValidationRules })),
          },
          {
            path: "configuration/administration/alert-calendar",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/AlertCalendar/AlertCalendar").then((m) => ({ Component: m.AlertCalendar })),
          },
          {
            path: "configuration/administration/iam-policy-categorization",
            HydrateFallback: SkeletonPage,
            lazy: () =>
              import("./pages/PrivilegeAccessGovernance/IamPolicyCategorization").then((m) => ({
                Component: m.IamPolicyCategorization,
              })),
          },
          {
            // Moved to Change Governance Assurance Validation; old links keep working.
            path: "configuration/administration/ai-analysis/*",
            loader: ({ params }) =>
              redirect(
                `/change-governance-assurance-validation/change-request-assessment/${params["*"] ?? ""}`,
              ),
          },
          {
            path: "configuration/administration/insights-configuration",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/DashboardConfig/DashboardConfig").then((m) => ({ Component: m.DashboardConfig })),
          },
          {
            path: "architecture-compliance",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/ArchitectureCompliance").then((m) => ({ Component: m.ArchitectureCompliance })),
          },

          {
            path: "vulnerability-management/overview",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/ComplianceDashboard").then((m) => ({ Component: m.VmOverview })),
          },
          {
            path: "vulnerability-management/dashboard",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/ComplianceDashboard").then((m) => ({ Component: m.VmDashboard })),
          },
          {
            path: "vulnerability-management/configuration",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/ComplianceDashboard").then((m) => ({ Component: m.VmToolsConfiguration })),
          },
          {
            path: "vulnerability-management/remediation-policy",
            loader: () => redirect("/vulnerability-management/configuration"),
          },
          {
            path: "vulnerability-management/schedulers",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/ComplianceDashboard").then((m) => ({ Component: m.VmSchedulers })),
          },
          {
            path: "configuration-management/overview",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/ComplianceDashboard").then((m) => ({ Component: m.CmOverview })),
          },
          {
            path: "configuration-management/dashboard",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/ComplianceDashboard").then((m) => ({ Component: m.CmDashboard })),
          },
          {
            path: "configuration-management/configuration",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/ComplianceDashboard").then((m) => ({ Component: m.CmToolsConfiguration })),
          },
          {
            path: "configuration-management/remediation-policy",
            loader: () => redirect("/configuration-management/configuration"),
          },
          {
            path: "configuration-management/schedulers",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/ComplianceDashboard").then((m) => ({ Component: m.CmSchedulers })),
          },
          { path: "vulnerability-management", loader: () => redirect("/vulnerability-management/overview") },
          { path: "configuration-management", loader: () => redirect("/configuration-management/overview") },
          {
            path: "it-compliance",
            loader: () => redirect("/vulnerability-management/overview"),
          },
          {
            path: "ciso-insights",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/CisoInsights").then((m) => ({ Component: m.CisoInsights })),
          },

          // ── Change Governance Assurance Validation ──────────────────────────────
          // TODO(rbac): gate these three routes once Log Review modules land in RBAC_MATRIX.
          { path: "change-governance-assurance-validation", Component: LogReviewPage },
          { path: "change-governance-assurance-validation-insights", Component: LogReviewerInsightsPage },
          { path: "change-governance-assurance-validation-ciso", Component: CisoLogReviewPage },

          // ── Security Tools Operations Center ──────────────────────────
          { path: "security-tools/agent-health", Component: AgentHealth },
          { path: "security-tools/asset-coverage-onboarding", Component: AssetCoverageOnboarding },
          {
            path: "security-tools/scan-coverage",
            HydrateFallback: SkeletonPage,
            lazy: () => import("./pages/ScanCoverage").then((m) => ({ Component: m.ScanCoverage })),
          },
          {
            path: "security-tools/configuration",
            HydrateFallback: SkeletonPage,
            lazy: () =>
              import("./pages/SecurityToolsConfiguration").then((m) => ({
                Component: m.SecurityToolsConfiguration,
              })),
          },
          // ── Change Assurance Governance ─────────────────────────────────
          // One destination with three routed tabs behind it. Nested rather
          // than three sibling paths: `/change-governance-assurance-validation-ciso`
          // shares a prefix with the base, which is what made every sibling
          // light up together in the sidebar.
          //
          // TODO(rbac): gate this subtree on change-governance-assurance-validation once
          // the Log Review modules land in RBAC_MATRIX.
          {
            path: "change-governance-assurance-validation",
            HydrateFallback: SkeletonPage,
            lazy: () =>
              import("./pages/ChangeAssuranceGovernance/ChangeAssuranceGovernancePage").then((m) => ({
                Component: m.ChangeAssuranceGovernancePage,
              })),
            children: [
              {
                index: true,
                HydrateFallback: SkeletonPage,
                lazy: () => import("./pages/LogReview/LogReviewPage").then((m) => ({ Component: m.LogReviewPage })),
              },
              {
                path: "reviewer-insights",
                HydrateFallback: SkeletonPage,
                lazy: () =>
                  import("./pages/LogReviewerInsights/LogReviewerInsightsPage").then((m) => ({
                    Component: m.LogReviewerInsightsPage,
                  })),
              },
              {
                path: "ciso-insights",
                HydrateFallback: SkeletonPage,
                lazy: () =>
                  import("./pages/CisoLogReview/CisoLogReviewPage").then((m) => ({
                    Component: m.CisoLogReviewPage,
                  })),
              },
            ],
          },
          {
            path: "change-governance-assurance-validation/change-request-assessment",
            HydrateFallback: SkeletonPage,
            lazy: () =>
              import("./pages/ChangeGovernanceAssuranceReadiness/pages/CGARList").then((m) => ({
                Component: m.default,
              })),
          },
          {
            path: "change-governance-assurance-validation/change-request-assessment/:crId",
            HydrateFallback: SkeletonPage,
            lazy: () =>
              import("./pages/ChangeGovernanceAssuranceReadiness/pages/CGARDetails").then((m) => ({
                Component: m.CGARDetails || m.default,
              })),
          },
          {
            path: "change-governance-assurance-validation/rules",
            HydrateFallback: SkeletonPage,
            lazy: () =>
              import("./pages/ChangeGovernanceAssuranceReadiness/pages/CGARrulemanagement").then((m) => ({
                Component: m.default,
              })),
          },
          {
            path: "demo/assessment-plan-full-trigger",
            HydrateFallback: SkeletonPage,
            lazy: () =>
              import("./pages/AssessmentPlanFullDemo").then((m) => ({
                Component: m.AssessmentPlanFullDemo,
              })),
          },
          {
            path: "cmdb/*",
            loader: ({ params, request }) =>
              redirect(
                `/configuration/cmdb/${params["*"] ?? ""}${new URL(request.url).search}`,
              ),
          },
          {
            path: "administration/*",
            loader: ({ params, request }) =>
              redirect(
                `/configuration/administration/${params["*"] ?? ""}${new URL(request.url).search}`,
              ),
          },
          {
            path: "administration/audit-log",
            loader: () => redirect("/configuration/audit-log"),
          },
          {
            path: "admin/validation-rules",
            loader: () => redirect("/configuration/administration/validation-rules"),
          },
        ],
      },
    ],
  },
  {
    // An unmatched URL must never silently bounce an authenticated user back to
    // the login form: that is what turned a typo'd redirect target into an
    // invisible "login does nothing" bug — no error, no toast, just the form
    // again. Only visitors without a session belong on /login. Authenticated
    // users go back to the last page that actually rendered (tracked in
    // MainLayout), falling back to the default dashboard if none is recorded.
    path: "*",
    loader: () => {
      if (!useAuthStore.getState().isAuthenticated) return redirect("/login");
      let lastVisited: string | null = null;
      try {
        if (typeof sessionStorage !== "undefined") {
          lastVisited = sessionStorage.getItem("lastVisitedPath");
        }
      } catch {
        lastVisited = null;
      }
      return redirect(lastVisited || "/dashboard/executive-insights");
    },
  },
]);
